import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { UserName, UserNameDTO } from "../types/userName";
import { BoUserRole } from "./boUserRole";
/* eslint-disable import/export */
export abstract class EditBOUserRPC implements
  LeoRPC<EditBOUserRPC.Request, EditBOUserRPC.Response, EditBOUserRPC.Errors.Errors>
{
  abstract execute(
    request: EditBOUserRPC.Request
  ): Promise<LeoRPCResult<EditBOUserRPC.Response, EditBOUserRPC.Errors.Errors>>;
}

export namespace EditBOUserRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    boUserId: string;
    userName: UserNameDTO;
    boUserRole: BoUserRole.BoUserRoleDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isEditBOUserRPCRequest: boolean = true;
  
    readonly boUserId: LeoUUID;
    readonly userName: UserName;
    readonly boUserRole: BoUserRole.BoUserRole;
  
  
    constructor(
    boUserId: LeoUUID, 
    userName: UserName, 
    boUserRole: BoUserRole.BoUserRole
    ) {
      this.boUserId = boUserId;
      this.userName = userName;
      this.boUserRole = boUserRole;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const boUserId = getLeoUuid(dto, "boUserId", InvalidRequestBoUserIdError);
      const userName = UserName.fromDTO(getObject(dto, "userName", InvalidRequestUserNameError));
      const boUserRole = BoUserRole.fromDTO(getObject(dto, "boUserRole", InvalidRequestBoUserRoleError));
      return new Request(
        boUserId,
        userName,
        boUserRole
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          boUserId: this.boUserId.toDTO(),
          userName: this.userName.toDTO(),
          boUserRole: BoUserRole.toDTO(this.boUserRole)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `EditBOUserRPC(boUserId='${this.boUserId}',userName='${this.userName}',boUserRole='${this.boUserRole}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `EditBOUserRPC.Response()`;
  };

  export enum RPCError {
    InvalidBoUserId = "INVALID_BO_USER_ID",
    CannotEditSelf = "CANNOT_EDIT_SELF",
    UnableToUpdateBoUser = "UNABLE_TO_UPDATE_BO_USER"
  }

  export namespace Errors {
    
    export interface InvalidBoUserIdDTO {
      code: string;
    }
    
    export class InvalidBoUserId extends LeoRPCError<RPCError> {
      readonly isInvalidBoUserId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidBoUserId;
        super(code);
      }
    
    
      toDTO(): InvalidBoUserIdDTO {
        const dto: InvalidBoUserIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidBoUserId.prototype.toString = function toString(): string {
      return `EditBOUserRPC.INVALID_BO_USER_ID()`;
    };
    
    
    export interface CannotEditSelfDTO {
      code: string;
    }
    
    export class CannotEditSelf extends LeoRPCError<RPCError> {
      readonly isCannotEditSelf: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.CannotEditSelf;
        super(code);
      }
    
    
      toDTO(): CannotEditSelfDTO {
        const dto: CannotEditSelfDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    CannotEditSelf.prototype.toString = function toString(): string {
      return `EditBOUserRPC.CANNOT_EDIT_SELF()`;
    };
    
    
    export interface UnableToUpdateBoUserDTO {
      code: string;
    }
    
    export class UnableToUpdateBoUser extends LeoRPCError<RPCError> {
      readonly isUnableToUpdateBoUser: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnableToUpdateBoUser;
        super(code);
      }
    
    
      toDTO(): UnableToUpdateBoUserDTO {
        const dto: UnableToUpdateBoUserDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnableToUpdateBoUser.prototype.toString = function toString(): string {
      return `EditBOUserRPC.UNABLE_TO_UPDATE_BO_USER()`;
    };
    

    export type Errors = InvalidBoUserId | CannotEditSelf | UnableToUpdateBoUser;
    export type ErrorsDTO = InvalidBoUserIdDTO | CannotEditSelfDTO | UnableToUpdateBoUserDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidBoUserId:
          return new InvalidBoUserId();
        case RPCError.CannotEditSelf:
          return new CannotEditSelf();
        case RPCError.UnableToUpdateBoUser:
          return new UnableToUpdateBoUser();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestBoUserIdError extends EditBOUserRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserIdError";
  }
}

export class InvalidRequestUserNameError extends EditBOUserRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUserNameError";
  }
}

export class InvalidRequestBoUserRoleError extends EditBOUserRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserRoleError";
  }
}


export class InvalidResponseInvalidBoUserIdError extends EditBOUserRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidBoUserIdError";
  }
}

export class InvalidResponseCannotEditSelfError extends EditBOUserRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseCannotEditSelfError";
  }
}

export class InvalidResponseUnableToUpdateBoUserError extends EditBOUserRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnableToUpdateBoUserError";
  }
}

